/**
 * Class action lawsuit search — MS Oram GET /legal/class-action-search
 * (company=… and/or industry=…, optional order_by).
 */

import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useSnackbar } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect, useRef, useContext } from 'react';

import {
  type ClassActionSearchApiResponse,
  type ClassActionSearchResponse,
} from '../../types';
import {
  getUIOrderByValue,
  normalizeAdvancedFilters,
  processExactMatchQuery,
} from '../../utils';
import { type ApiFilters, prepareQueryForCacheKey } from '../utils/filterUtils';

import {
  API_CONFIG,
  ERROR_MESSAGES,
  makeAuthenticatedGetRequest,
  resetSearchRefAfterDelay,
  useBaseCourtApi,
} from './useBaseCourtApi';

const CLASS_ACTION_SEARCH_SEGMENT = 'class-action-search';

const normalizeResponse = (
  data: ClassActionSearchApiResponse
): ClassActionSearchResponse => ({
  ...data,
  results: data.cards,
});

export type ClassActionSearchQueryParam = 'company' | 'industry';

/** Unprefixed queries map to this param; matches UI search type `company`. */
export const CLASS_ACTION_UNPREFIXED_QUERY_PARAM: ClassActionSearchQueryParam =
  'company';

export interface ParsedClassActionSearchQuery {
  param: ClassActionSearchQueryParam;
  value: string;
}

/**
 * Parses `company:` / `industry:` prefixes. Unprefixed input uses
 * {@link CLASS_ACTION_UNPREFIXED_QUERY_PARAM} (same semantics as the UI
 * "Company" search type).
 */
export const parseClassActionSearchQuery = (
  raw: string
): ParsedClassActionSearchQuery | null => {
  const t = raw.trim();
  if (!t) {
    return null;
  }
  const lower = t.toLowerCase();
  if (lower.startsWith('company:')) {
    const value = t.slice(8).trim();
    return value ? { param: 'company', value } : null;
  }
  if (lower.startsWith('industry:')) {
    const value = t.slice(9).trim();
    return value ? { param: 'industry', value } : null;
  }
  return { param: CLASS_ACTION_UNPREFIXED_QUERY_PARAM, value: t };
};

const appendClassActionQueryParams = (
  params: URLSearchParams,
  filters: ApiFilters
): void => {
  const orderBy = filters.order_by;
  if (typeof orderBy === 'string' && orderBy.trim()) {
    params.set('order_by', orderBy.trim());
  }
};

export const useClassActionLawsuitSearch = (
  initialQuery: string = '',
  exactMatch: boolean = true
) => {
  const [sortOption, setSortOption] = useState('dateFiled_desc');
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentFilters, setCurrentFilters] = useState<ApiFilters>({});
  const [results, setResults] = useState<ClassActionSearchResponse | null>(
    null
  );
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentExactMatch, setCurrentExactMatch] = useState(exactMatch);

  const isSearchingRef = useRef(false);
  const isUpdatingStateRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useContext(AppConfig);
  const {
    makeApiCall,
    isLoading: isTokenLoading,
    tokenData,
  } = useBaseCourtApi();

  const parsedQuery = parseClassActionSearchQuery(currentQuery);
  const hasValidSearchParams = parsedQuery !== null;
  const shouldExecuteQuery =
    searchExecuted && hasValidSearchParams && !isUpdatingStateRef.current;

  const handleSearchError = (error: unknown, context: string) => {
    const isExpectedError =
      error instanceof Error &&
      error.message?.includes('Search query is required');

    if (!isExpectedError) {
      logError(`Class action search error in ${context}`, error as Error);
      enqueueSnackbar(ERROR_MESSAGES.GENERAL_ERROR, { variant: 'error' });
    }
  };

  const executeSearch = async (): Promise<ClassActionSearchResponse> => {
    const parsed = parseClassActionSearchQuery(currentQuery);
    if (!parsed) {
      throw new Error('Search query is required');
    }
    const apiValue = processExactMatchQuery(parsed.value, currentExactMatch);

    return makeApiCall(async (token: string) => {
      const params = new URLSearchParams();
      params.set(parsed.param, apiValue);
      const orderByFromSort = normalizeAdvancedFilters(
        { order_by: sortOption },
        false,
        false
      ) as ApiFilters;
      const hasOrderFromSort =
        typeof orderByFromSort.order_by === 'string' &&
        orderByFromSort.order_by.trim().length > 0;
      const hasOrderFromFilters =
        typeof currentFilters.order_by === 'string' &&
        currentFilters.order_by.trim().length > 0;
      const filtersForOrderBy: ApiFilters = hasOrderFromSort
        ? orderByFromSort
        : hasOrderFromFilters
        ? { order_by: currentFilters.order_by }
        : orderByFromSort;
      appendClassActionQueryParams(params, filtersForOrderBy);
      const url = `${
        API_CONFIG.BASE_URL
      }/legal/${CLASS_ACTION_SEARCH_SEGMENT}?${params.toString()}`;
      const raw =
        await makeAuthenticatedGetRequest<ClassActionSearchApiResponse>(
          url,
          token
        );
      return normalizeResponse(raw);
    });
  };

  const filtersForCacheKey = Object.fromEntries(
    Object.entries(currentFilters).filter(([key]) => key !== 'order_by')
  );
  const filtersKey = Object.keys(filtersForCacheKey).length
    ? JSON.stringify(filtersForCacheKey)
    : 'no-filters';
  const queryKeyQuery = prepareQueryForCacheKey(
    currentQuery,
    currentExactMatch
  );
  const queryKey = [
    'class-action-lawsuits-v2',
    queryKeyQuery,
    sortOption,
    filtersKey,
  ];

  const searchQuery = useQuery(queryKey, executeSearch, {
    enabled: shouldExecuteQuery && !isTokenLoading && !!tokenData?.token,
    staleTime: API_CONFIG.STALE_TIME,
    cacheTime: API_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: unknown) => {
      const isExpectedError =
        error instanceof Error &&
        error.message?.includes('Search query is required');
      return !isExpectedError && failureCount < API_CONFIG.MAX_RETRIES;
    },
    onSuccess: (data: ClassActionSearchResponse) => {
      setResults(data);
      setSearchExecuted(false);
    },
    onError: (error: unknown) => {
      handleSearchError(error, 'search query execution');
      setSearchExecuted(false);
      setResults(null);
    },
  });

  useEffect(() => {
    if (currentExactMatch !== exactMatch) {
      setCurrentExactMatch(exactMatch);
      if (currentQuery.trim() && searchQuery.data) {
        searchQuery.refetch();
      }
    }
  }, [exactMatch, currentQuery, currentExactMatch, searchQuery]);

  useEffect(() => {
    if (initialQuery?.trim()) {
      isUpdatingStateRef.current = true;
      setCurrentQuery(initialQuery);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (searchQuery.data && !searchQuery.isLoading) {
      setResults(searchQuery.data);
    }
  }, [searchQuery.data, searchQuery.isLoading]);

  const triggerCombinedSearch = (
    query: string,
    filters: Record<string, unknown> = {}
  ) => {
    isSearchingRef.current = true;
    isUpdatingStateRef.current = true;

    const apiReadyFilters = normalizeAdvancedFilters(
      filters,
      false,
      false
    ) as ApiFilters;
    setCurrentFilters(apiReadyFilters);

    if (
      apiReadyFilters.order_by &&
      typeof apiReadyFilters.order_by === 'string'
    ) {
      const uiOrderBy = getUIOrderByValue(
        apiReadyFilters.order_by,
        false,
        false
      );
      if (
        uiOrderBy !== sortOption &&
        (uiOrderBy === 'dateFiled_desc' || uiOrderBy === 'dateFiled_asc')
      ) {
        setSortOption(uiOrderBy);
      }
    }

    setCurrentQuery(query);
    setTimeout(() => {
      isUpdatingStateRef.current = false;
      setSearchExecuted(true);
    }, 0);
    resetSearchRefAfterDelay(isSearchingRef);
  };

  const updateResults = (
    newResults: ClassActionSearchResponse | ClassActionSearchApiResponse
  ) => {
    const normalized: ClassActionSearchResponse =
      'results' in newResults && Array.isArray(newResults.results)
        ? newResults
        : normalizeResponse(newResults);
    setResults(normalized);
    setIsPaginationLoading(false);
  };

  const isSearching = searchExecuted && searchQuery.isLoading;
  const hasResults = !!results;
  const isExpectedQueryError =
    searchQuery.error instanceof Error &&
    searchQuery.error.message?.includes('Search query is required');

  return {
    classActionLawsuitResults: results,
    searchQuery: currentQuery,
    sortOption,

    isSearching,
    isAnyLoading: isSearching || isPaginationLoading,
    hasResults,
    hasError: !!searchQuery.error,
    generalError:
      searchQuery.error && !isExpectedQueryError
        ? ERROR_MESSAGES.GENERAL_ERROR
        : null,
    isTokenLoading,
    isPaginationLoading,

    triggerCombinedSearch,
    setSortOption,
    updateClassActionLawsuitResults: updateResults,
    setPaginationLoading: setIsPaginationLoading,
    setSearchExecuted,

    error: searchQuery.error,
    data: searchQuery.data,
    refetch: searchQuery.refetch,
  };
};
